import { useState } from 'react';

import RotuloDeFiltro from 'components/ui/rotulo-de-filtro';
import SeletorDeMes, { TODOS_OS_MESES } from 'components/ui/seletor-de-mes';
import { useAuth } from 'context/AuthContext';
import { NotFound } from 'pages/NotFound';
import { FaseDoCiclo } from 'pages/RealizacaoDestaque/realizacoes-do-semestre';
import { getMissaoEquipePrefixo } from 'services/achievement.service';
import { CachedPrefixAchievement } from 'services/home-feed.service';
import { textoDaContagem } from 'utils/strings';

import { CartaoDeRealizacao, ESTILO_DO_CARTAO } from './components/UltimasRealizacoes';
import PaginaDoFeed from './components/PaginaDoFeed';
import { PaginaLida, useListaDoPrefixo } from './useListaDoPrefixo';

export const REALIZACOES_POR_PAGINA = 12;

/**
 * Página do `extrato_missao_equipe_prefixo` no ciclo do app, sem busca: a busca do aggregate deixa de filtrar pelo
 * prefixo e varre o banco (comportamento do objeto).
 */
async function lerRealizacoes(prefixo: number, cycleId: string, faseId: string, pagina: number): Promise<PaginaLida<CachedPrefixAchievement>> {
  const { result, range } = await getMissaoEquipePrefixo(
    prefixo,
    undefined,
    undefined,
    cycleId,
    faseId || undefined,
    undefined,
    pagina * REALIZACOES_POR_PAGINA,
    null,
    REALIZACOES_POR_PAGINA,
  );

  return { itens: Array.isArray(result) ? result : [], total: Number.isFinite(range.total) ? range.total : 0 };
}

/** Todas as Realizações Destaque do prefixo de quem acessa no ciclo, com filtro por mês (AJ3-09 a AJ3-14). */
export default function RealizacoesDoPrefixo() {
  const { user, userInfo, cycleSelected, cyclesExtended } = useAuth();

  const prefixo: number | undefined = userInfo?.prefixo;
  const cycleId = cycleSelected?._id;

  // AJ3-11: o mês é do ciclo. Trocar o ciclo do app (inclusive pelo menu) volta a "Todos os meses": o filtro guarda o
  // ciclo em que foi escolhido e é zerado no render em que o ciclo muda, então a leitura nunca sai com a fase de outro
  // ciclo e voltar ao ciclo anterior não traz o mês de volta.
  const [mesDoCiclo, setMesDoCiclo] = useState({ cycleId, faseId: TODOS_OS_MESES });
  if (mesDoCiclo.cycleId !== cycleId) setMesDoCiclo({ cycleId, faseId: TODOS_OS_MESES });
  const faseId = mesDoCiclo.cycleId === cycleId ? mesDoCiclo.faseId : TODOS_OS_MESES;
  const escolherMes = (id: string) => setMesDoCiclo({ cycleId, faseId: id });

  const fases = (cyclesExtended?.find(ciclo => ciclo._id === cycleId) as { steps?: FaseDoCiclo[] } | undefined)?.steps;

  const lista = useListaDoPrefixo(leitorDaPagina(), `${cycleId}|${faseId}|${prefixo}`, REALIZACOES_POR_PAGINA);

  // Espera o ciclo e o `find/info`; carregado e sem prefixo, a lista fica vazia. Quem é da Rede não lê nada.
  function leitorDaPagina() {
    if (user?.extra?.rede || !cycleId || !userInfo) return undefined;
    if (!prefixo) return async () => ({ itens: [], total: 0 });
    return (pagina: number) => lerRealizacoes(prefixo, cycleId, faseId, pagina);
  }

  // Mesma guarda da página antiga: o perfil rede não participa de Realização Destaque.
  if (user?.extra?.rede) return <NotFound />;

  return (
    <PaginaDoFeed
      titulo={prefixo ? `Realizações destaque do prefixo ${prefixo}` : 'Realizações destaque do prefixo'}
      estado={lista.estado}
      textoDoErro="Não foi possível carregar as realizações do prefixo."
      textoDoVazio="Ainda não há realizações destaque no seu prefixo neste ciclo."
      onTentarDeNovo={lista.tentarDeNovo}
      resumo={textoDaContagem(lista.total, 'realização', 'realizações')}
      filtros={
        <div className="mb-3.5 w-full max-w-[280px]">
          <RotuloDeFiltro htmlFor="realizacoes-do-prefixo-mes">Mês / fase</RotuloDeFiltro>
          <SeletorDeMes fases={fases} valor={faseId} onChange={escolherMes} rotulo="Mês / fase" inputId="realizacoes-do-prefixo-mes" />
        </div>
      }
      totalDePaginas={Math.ceil(lista.total / REALIZACOES_POR_PAGINA)}
      pagina={lista.pagina}
      onPagina={lista.irParaPagina}
      rotuloDaPaginacao="Paginação das realizações do prefixo"
    >
      <ul className="m-0 grid list-none grid-cols-1 gap-5 px-1 pb-3 pt-2 md:grid-cols-3">
        {lista.itens.map((item, indice) => (
          <CartaoDeRealizacao key={item._id} item={item} estilo={ESTILO_DO_CARTAO[indice % ESTILO_DO_CARTAO.length]} />
        ))}
      </ul>
    </PaginaDoFeed>
  );
}
